/**
 * ChatPulseHome — dedicated /chat page.
 *
 * Two sections:
 *   • Hot in the last 24h (post count)
 *   • Recent activity across all contexts
 */
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Flame, Loader2, ExternalLink, Search, X } from "lucide-react";

const BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/chatpulse/v1`;

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (!then) return "";
  const s = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

const CONTEXT_LINK = {
  event:    (id) => `/events/${id}`,
  article:  (id) => `/publisher/article/${id}`,
  location: (id) => `/l/${id.replace(/-/g, "/")}`,
  topic:    (id) => `/topic/${id}`,
  site:     () => `/`,
};

export default function ChatPulseHome() {
  const [hot, setHot] = useState(null);
  const [recent, setRecent] = useState(null);
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [h, r] = await Promise.all([
          fetch(`${BASE}/hot?limit=10`).then((x) => x.json()),
          fetch(`${BASE}/timeline?limit=25`).then((x) => x.json()),
        ]);
        setHot(h.threads || []);
        setRecent(r.threads || []);
      } catch {
        setHot([]);
        setRecent([]);
      }
    })();
  }, []);

  // Debounced live search — fires ~350ms after the last keystroke.
  const runSearch = useCallback(async (q) => {
    if (q.trim().length < 2) {
      setSearchResults(null);
      return;
    }
    setSearching(true);
    try {
      const r = await fetch(`${BASE}/search?q=${encodeURIComponent(q.trim())}&limit=20`);
      const d = await r.json();
      setSearchResults(d.threads || []);
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    const h = window.setTimeout(() => runSearch(query), 350);
    return () => window.clearTimeout(h);
  }, [query, runSearch]);

  const inSearchMode = query.trim().length >= 2;

  return (
    <div className="max-w-4xl mx-auto px-4 lg:px-6 py-8" data-testid="chatpulse-home">
      <header className="pb-5 border-b border-slate-200 mb-6">
        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold flex items-center gap-1.5">
          <MessageCircle className="w-3.5 h-3.5" /> ChatsPulse · Community forum
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-1">
          Every discussion, in one feed
        </h1>
        <p className="text-sm text-slate-600 mt-1.5 max-w-2xl">
          Threads from every SocialPulse property — news events, published
          blogs, cities, and topics. Read anonymously. Join in when
          you're signed in.
        </p>

        {/* Search bar */}
        <div className="mt-5 relative max-w-xl" data-testid="chatpulse-search">
          <Search className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search threads by keyword…"
            className="w-full pl-11 pr-10 py-2.5 text-sm border border-slate-200 rounded-full outline-none focus:border-blue-400 bg-white"
            data-testid="chatpulse-search-input"
            aria-label="Search discussions"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full hover:bg-slate-100 inline-flex items-center justify-center"
              aria-label="Clear search"
              data-testid="chatpulse-search-clear"
            >
              <X className="w-3.5 h-3.5 text-slate-500" />
            </button>
          )}
        </div>
      </header>

      {inSearchMode ? (
        <section data-testid="chatpulse-search-results">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold text-slate-900">
              Results for <span className="text-blue-700">"{query.trim()}"</span>
            </h2>
            {searching && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
          </div>
          {searchResults === null ? (
            <Loading />
          ) : searchResults.length === 0 ? (
            <div className="text-sm italic text-slate-500 py-6" data-testid="chatpulse-search-empty">
              No threads mention "{query.trim()}" yet.
            </div>
          ) : (
            <ul className="space-y-3">
              {searchResults.map((t) => (
                <li key={t.id}>
                  <ThreadCard thread={t} testIdPrefix="chatpulse-search" showSnippet />
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : (
      <>
      {/* Hot */}
      <section className="mb-8" data-testid="chatpulse-home-hot">
        <h2 className="text-sm font-bold text-slate-900 inline-flex items-center gap-1.5 mb-3">
          <Flame className="w-4 h-4 text-red-500" /> Hot in the last 24 hours
        </h2>
        {hot === null ? (
          <Loading />
        ) : hot.length === 0 ? (
          <div className="text-sm italic text-slate-500 py-4">
            No hot threads yet — go start one!
          </div>
        ) : (
          <ol className="space-y-3">
            {hot.map((t, i) => (
              <li key={t.id}>
                <ThreadCard rank={i + 1} thread={t} testIdPrefix="chatpulse-hot" />
              </li>
            ))}
          </ol>
        )}
      </section>

      {/* Recent */}
      <section data-testid="chatpulse-home-recent">
        <h2 className="text-sm font-bold text-slate-900 inline-flex items-center gap-1.5 mb-3">
          <MessageCircle className="w-4 h-4 text-blue-600" /> Recent activity
        </h2>
        {recent === null ? (
          <Loading />
        ) : recent.length === 0 ? (
          <div className="text-sm italic text-slate-500 py-4">
            Nothing yet — the first thread posted on any site will land here.
          </div>
        ) : (
          <ul className="space-y-3">
            {recent.map((t) => (
              <li key={t.id}>
                <ThreadCard thread={t} testIdPrefix="chatpulse-recent" />
              </li>
            ))}
          </ul>
        )}
      </section>
      </>
      )}
    </div>
  );
}


function Loading() {
  return (
    <div className="py-6 flex items-center gap-2 text-xs text-slate-400">
      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
    </div>
  );
}


function ThreadCard({ thread, rank, testIdPrefix, showSnippet = false }) {
  const surfaceHref = (CONTEXT_LINK[thread.context_type] || (() => "/"))(thread.context_id);
  return (
    <article
      className="flex items-start gap-3 p-3 rounded-2xl border border-slate-200 bg-white hover:border-slate-400 transition-colors"
      data-testid={`${testIdPrefix}-thread-${thread.id}`}
    >
      {rank && (
        <span className="w-7 h-7 rounded-full bg-red-100 text-red-700 inline-flex items-center justify-center text-xs font-black shrink-0">
          {rank}
        </span>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-0.5">
          <span className="text-blue-700">{thread.context_type}</span>
          {thread.context_label && (
            <>
              <span className="text-slate-300">·</span>
              <span className="text-slate-500 truncate max-w-[220px]">{thread.context_label}</span>
            </>
          )}
        </div>
        <h3 className="text-sm font-semibold text-slate-900 leading-snug">
          {thread.title}
        </h3>
        {showSnippet && thread.match_snippet && thread.match_where === "post" && (
          <p
            className="text-xs text-slate-600 mt-1 italic border-l-2 border-blue-200 pl-2 line-clamp-2"
            data-testid={`${testIdPrefix}-snippet-${thread.id}`}
          >
            "{thread.match_snippet}"
          </p>
        )}
        <div className="text-xs text-slate-500 mt-1">
          {thread.author?.name || "Anon"} · {thread.post_count} post{thread.post_count === 1 ? "" : "s"}
          {thread.reaction_count > 0 && ` · ${thread.reaction_count} 👍`}
          {thread.last_post_at && ` · ${timeAgo(thread.last_post_at)}`}
        </div>
      </div>
      <div className="flex flex-col items-end gap-1 shrink-0">
        <Link
          to={surfaceHref}
          className="text-slate-400 hover:text-blue-700"
          title="Go to the source page"
          data-testid={`${testIdPrefix}-surface-${thread.id}`}
        >
          <ExternalLink className="w-4 h-4" />
        </Link>
        <a
          href={thread.canonical_url}
          target="_blank"
          rel="noopener"
          className="text-[10px] uppercase tracking-widest text-slate-400 hover:text-blue-700 font-semibold"
        >
          Chat
        </a>
      </div>
    </article>
  );
}
